import toast from 'react-hot-toast';
import type { RecordModel } from 'pocketbase';
import pb from '@libs/pocketbase';
import type {
  PackageRecord,
  PackageFilters,
  CreatePackagePayload,
  UpdatePackagePayload,
  FunctionRecord,
  PackageFunctionRecord,
} from './types';

const packagesCollection = () => pb.collection('packages');
const packageFunctionsCollection = () => pb.collection('package_functions');
const functionsCollection = () => pb.collection('functions');

type PackageRecordModel = RecordModel & {
  name: string;
  code: string;
  origin_price: number;
  sale_price: number;
  position?: number;
  description?: string;
  status?: 'active' | 'inactive';
  is_deleted?: boolean;
  created_by?: string;
  updated_by?: string;
  created_at?: string;
  updated_at?: string;
};

const mapPackageRecord = (
  record: PackageRecordModel,
  functionsCount = 0,
  functions?: FunctionRecord[]
): PackageRecord => ({
  id: record.id,
  name: record.name,
  code: record.code,
  origin_price: Number(record.origin_price ?? 0),
  sale_price: Number(record.sale_price ?? 0),
  position: typeof record.position === 'number' ? record.position : undefined,
  description: record.description,
  status: (record.status ?? 'active') as 'active' | 'inactive',
  is_deleted: record.is_deleted ?? false,
  created_by: record.created_by,
  updated_by: record.updated_by,
  created_at: record.created_at,
  updated_at: record.updated_at,
  functions_count: functionsCount,
  functions,
});

const sanitizeFilter = (filters?: PackageFilters) => {
  const clauses = ['is_deleted!=true'];
  if (filters?.status && filters.status !== 'all') {
    clauses.push(`status="${filters.status}"`);
  }
  if (filters?.search) {
    const keyword = filters.search.replace(/"/g, '\\"');
    clauses.push(`(name~"${keyword}"||code~"${keyword}")`);
  }
  return clauses.join('&&');
};

const fetchPackageFunctionCounts = async (packageIds: string[]) => {
  if (packageIds.length === 0) {
    return {};
  }

  const filter = packageIds.map((id) => `package_id="${id}"`).join('||');
  const records = await packageFunctionsCollection().getFullList<PackageFunctionRecord>({
    filter,
    fields: 'id,package_id,function_id',
    requestKey: undefined,
  });

  return records.reduce<Record<string, number>>((acc, record) => {
    const pkgId = record.package_id;
    acc[pkgId] = (acc[pkgId] ?? 0) + 1;
    return acc;
  }, {});
};

const fetchPackageFunctions = async (packageId: string) => {
  const pivotRecords = await packageFunctionsCollection().getFullList<PackageFunctionRecord>({
    filter: `package_id="${packageId}"`,
    requestKey: undefined,
  });

  if (pivotRecords.length === 0) return [];

  const functionIds = pivotRecords.map((item) => item.function_id);
  const filter = functionIds.map((id) => `id="${id}"`).join('||');
  const functions = await functionsCollection().getFullList<FunctionRecord>({
    filter,
    requestKey: undefined,
  });
  return functions;
};

const upsertPackageFunctions = async (packageId: string, nextFunctionIds: string[]) => {
  const existing = await packageFunctionsCollection().getFullList<PackageFunctionRecord>({
    filter: `package_id="${packageId}"`,
    fields: 'id,function_id',
    requestKey: undefined,
  });

  const existingMap = new Map(existing.map((pf) => [pf.function_id, pf]));
  const nextSet = new Set(nextFunctionIds);

  const toDelete = existing.filter((pf) => !nextSet.has(pf.function_id));
  const toCreate = nextFunctionIds.filter((id) => !existingMap.has(id));

  await Promise.all([
    ...toDelete.map((pf) => packageFunctionsCollection().delete(pf.id)),
    ...toCreate.map((functionId) =>
      packageFunctionsCollection().create({
        package_id: packageId,
        function_id: functionId,
      })
    ),
  ]);
};

export const packageApi = {
  async list(filters?: PackageFilters): Promise<PackageRecord[]> {
    const filter = sanitizeFilter(filters);
    const page = await packagesCollection().getList<PackageRecordModel>(1, 50, {
      filter,
      sort: '-created_at',
    });

    const counts = await fetchPackageFunctionCounts(page.items.map((item) => item.id));
    return page.items.map((item) => mapPackageRecord(item, counts[item.id] ?? 0));
  },

  async get(id: string): Promise<PackageRecord> {
    const record = await packagesCollection().getOne<PackageRecordModel>(id);
    const functions = await fetchPackageFunctions(id);
    return mapPackageRecord(record, functions.length, functions);
  },

  async create(payload: CreatePackagePayload): Promise<PackageRecord> {
    const { function_ids, ...packagePayload } = payload;
    try {
      const created = await packagesCollection().create(packagePayload);
      let functions: FunctionRecord[] = [];

      // Tạo package_functions, nếu lỗi vẫn tiếp tục
      if (function_ids?.length) {
        try {
          await Promise.all(
            function_ids.map((functionId) =>
              packageFunctionsCollection().create({
                package_id: created.id,
                function_id: functionId,
              })
            )
          );
          // Fetch functions sau khi tạo thành công
          functions = await fetchPackageFunctions(created.id);
        } catch (funcError) {
          // Log lỗi nhưng không throw, package đã tạo thành công
          console.error('Error creating package functions:', funcError);
          // Vẫn fetch functions hiện có (nếu có)
          functions = await fetchPackageFunctions(created.id).catch(() => []);
        }
      }

      toast.success('Tạo gói dịch vụ thành công');

      // Trả về data từ record đã tạo, không gọi this.get()
      return mapPackageRecord(created as PackageRecordModel, functions.length, functions);
    } catch (error: any) {
      // Kiểm tra nếu là lỗi autocancelled thì không hiển thị toast
      const isAutocancelled =
        error?.message?.includes('autocancelled') ||
        error?.message?.includes('autocancel') ||
        error?.status === 0 ||
        error?.response?.status === 0;

      if (!isAutocancelled) {
        toast.error('Không thể tạo gói dịch vụ');
      }
      throw error;
    }
  },

  async update(id: string, payload: UpdatePackagePayload): Promise<PackageRecord> {
    const { function_ids, ...packagePayload } = payload;
    try {
      await packagesCollection().update(id, packagePayload);
      if (function_ids) {
        await upsertPackageFunctions(id, function_ids);
      }
      toast.success('Cập nhật gói dịch vụ thành công');
      return this.get(id);
    } catch (error) {
      toast.error('Không thể cập nhật gói dịch vụ');
      throw error;
    }
  },

  async delete(id: string): Promise<PackageRecord> {
    try {
      const record = await packagesCollection().update(id, {
        is_deleted: true,
        status: 'inactive',
      });
      toast.success('Đã vô hiệu hóa gói (soft delete)');
      return mapPackageRecord(record as PackageRecordModel);
    } catch (error) {
      toast.error('Không thể xóa gói');
      throw error;
    }
  },

  async activate(id: string): Promise<PackageRecord> {
    try {
      const record = await packagesCollection().update(id, { status: 'active', is_deleted: false });
      toast.success('Đã kích hoạt gói');
      return mapPackageRecord(record as PackageRecordModel);
    } catch (error) {
      toast.error('Không thể kích hoạt gói');
      throw error;
    }
  },

  async deactivate(id: string): Promise<PackageRecord> {
    try {
      const record = await packagesCollection().update(id, { status: 'inactive' });
      toast.success('Đã vô hiệu hóa gói');
      return mapPackageRecord(record as PackageRecordModel);
    } catch (error) {
      toast.error('Không thể vô hiệu hóa gói');
      throw error;
    }
  },

  async getFunctions(): Promise<FunctionRecord[]> {
    try {
      return await functionsCollection().getFullList<FunctionRecord>({
        filter: 'is_deleted!=true',
        sort: 'module_id,position',
      });
    } catch (error) {
      console.error('getFunctions error', error);
      return [];
    }
  },

  async getPackageFunctions(packageId: string): Promise<PackageFunctionRecord[]> {
    return packageFunctionsCollection().getFullList({
      filter: `package_id="${packageId}"`,
    });
  },
};


